import type { Palette } from "@mui/material"
import { DateTime } from "luxon"
import fp from "lodash/fp"

import type { GaugeConfig } from "types/gaugePlot.types"
import type { ColorType, GaugePlotView, RecordType } from "types/dashboard.types"
import {
  formatBatteryStatusData,
  formatSignalStatusData,
} from "helpers/utils/telemetries"
import { getLegendParts } from "helpers/utils/plots"

interface GaugeConfigParams {
  view: GaugePlotView
  data: RecordType[]
  palette: Palette
}

export function gaugeConfig({ view, data, palette }: GaugeConfigParams): GaugeConfig {
  const name = view.name.toLowerCase().replace(/\s/g, "")
  const viewProperties = view.properties

  const dangerToGoodGradient = [
    { offset: 0, color: palette.veryWeak.main },
    { offset: 0.5, color: palette.fair.main },
    { offset: 1, color: palette.strong.main },
  ]

  const getFormattedData = (data: RecordType[]) => {
    if ("nameKey" in view) {
      if (view.nameKey === "SIGNAL_LEVEL") {
        return formatSignalStatusData(data)
      }
      if (view.nameKey === "BATTERY_STATUS") {
        return formatBatteryStatusData(data)
      }
    }
    return data
  }

  const formattedData = getFormattedData(data)

  const fill = {
    name,
    gradient: {
      colorGradient: dangerToGoodGradient,
    },
  }

  const getDomain = (colors: ColorType[]) => {
    const colorValues = colors?.map((color) => color.value)
    return {
      min: Math.min(...colorValues),
      max: Math.max(...colorValues),
    }
  }

  const formatValue = (value?: string | number | string[]): number => {
    if (typeof value === "string") {
      const valueFormatted = Number.parseFloat(value)
      return !Number.isNaN(valueFormatted) ? valueFormatted : 0
    }
    if (typeof value === "number") {
      return value
    }
    if (Array.isArray(value)) {
      const valueFormatted = Number.parseFloat(value[value.length - 1])
      return !Number.isNaN(valueFormatted) ? valueFormatted : 0
    }
    return 0 // in this case it's undefined
  }

  const getPlotFooter = (data: RecordType[]) => {
    if (!viewProperties.footerFields?.includes("_time")) {
      return undefined
    }
    const time = data[0]?._time
    if (fp.isString(time)) {
      return DateTime.fromISO(time).toFormat("yyyy-MM-dd HH:mm:ss")
    } else if (fp.isDate(time)) {
      return DateTime.fromJSDate(time).toFormat("yyyy-MM-dd HH:mm:ss")
    }
    return undefined
  }

  const legendParts = getLegendParts(formattedData)
  const domain = getDomain(viewProperties.colors)
  const rawValue = formatValue(formattedData[0]?._value)

  const config = {
    cellBG: {
      fill: palette.gaugeBG.main,
      filter: palette.grey[500],
    },
    decimalPlaces: viewProperties.decimalPlaces ?? 0,
    fill,
    domain,
    plotSize: { xs: 240, sm: 295, md: 310, lg: 340 },
    // MUI Charts angle convention: 0° = 12 o'clock, clockwise positive
    range: { min: -120, max: 120 },
    ticks: {
      valueInterval: viewProperties.ticksValueInterval,
      labelCount: viewProperties.ticksLabelCount,
      fill: palette.text.primary,
      prefix: viewProperties.tickPrefix || "",
      suffix: viewProperties.tickSuffix || "",
    },
    values: {
      footer: getPlotFooter(formattedData),
      legend: legendParts?.legend,
      legendParts,
      value: rawValue,
      fill: palette.text.primary,
      prefix: viewProperties.prefix ?? "",
      suffix: viewProperties.suffix ?? "",
    },
  }

  return config
}
